/**
 * ***********************************************************************
 * State-level GVW / Vehicle Weight / GVWR / MPG requirement checks.
 *
 * This is a direct port of the bot's `state_requirements.py`
 * (DMV-bot-docs / DMV-bot-fees / DMV-bot-refi-docs / DMV-bot-refi-fees),
 * which in turn mirrors ATC Granular Fee Tax Service 3.0 - API Guide
 * v4.0.0 (17-Jul-2023), Section 5 "Specific State Requirements"
 * (ATC_GVW_Weight_MPG_State_Requirements.xlsx).
 *
 * The bot returns `warning_response()` and tells the user to "visit our
 * frontend portal to manually complete this request" whenever one of
 * these data points is required for the target state + vehicle type and
 * is still blank after the VIN decode. This module is what makes that
 * instruction true: the portal now asks for exactly the same fields the
 * bot would have bounced on, so a user who follows the link cannot
 * submit a request that is still missing them.
 *
 * IMPORTANT: GVW_REQUIREMENTS, WEIGHT_REQUIREMENTS and MPG_REQUIREMENTS below
 * must stay in lockstep with `state_requirements.py`. If the ATC guide is
 * revised, update both. GVWR_REQUIREMENTS has a different source — the Jinja
 * Fees page's own state field map — and is documented at its definition.
 * ***********************************************************************
 */

// Rule shape: [kind, codes]
//   ['all',        null]     -> applies to every vehicle type
//   ['all_except', Set]      -> applies to every vehicle type except those listed
//   ['only',       Set]      -> applies only to the vehicle types listed
//
// Vehicle type codes follow the ATC guide legend:
//   PV=Passenger Vehicle, LT=Pickup Truck, M=Motorcycle, T=Trailer,
//   O=Motorhome, CC=Cab & Chassis, SUV=Sports Utility Vehicle, TR=Truck.
//   DV / PU / 5W / CR appear in the guide's requirement tables but not in
//   its legend; they are kept so user-supplied types still match.
const ALL = 'all';
const ALL_EXCEPT = 'all_except';
const ONLY = 'only';

const only = (...codes) => [ONLY, new Set(codes)];
const allExcept = (...codes) => [ALL_EXCEPT, new Set(codes)];
const all = () => [ALL, null];

export const GVW_REQUIREMENTS = {
  AK: all(),                                       // MVRT - all vehicle types
  AL: only('LT', 'CC', 'DV', 'O'),
  AR: only('LT', 'CC', 'DV', 'O', 'TR'),
  AZ: only('LT', 'CC', 'DV', 'O', 'T'),
  CA: all(),                                       // Weight Fee (LT CC DV O TR) + CTIP (not specified)
  CO: only('TR', 'LT', '5W', 'CR'),
  CT: only('PV', 'SUV', 'TR', 'T'),                // Title (T) + Registration (PV SUV TR T)
  DC: only('LT', 'TR', 'CC', 'T'),                 // "Commercial Vehicles" (see py note)
  FL: only('CC', 'LT', 'TR', 'DV'),
  GA: allExcept('PV', 'SUV'),
  HI: all(),                                       // State/County Weight Fees (not specified)
  IA: only('TR'),
  ID: only('LT', 'CC', 'DV', 'TR'),
  IL: only('LT', 'CC', 'DV', 'T', 'TR'),
  IN: only('LT', 'CC', 'DV', 'TR'),
  LA: only('PU', 'LT', 'CC', 'DV', 'TR'),          // guide uses PU (likely Pickup); LT included to match decode
  KY: only('PV', 'SUV', 'LT', 'CC', 'DV', 'TR'),
  MA: only('T', 'PV', 'SUV', 'LT', 'CC', 'DV', 'TR'), // Title (T) + Registration
  MD: only('LT', 'CC', 'DV', 'T', 'TR'),
  ME: only('LT', 'CC', 'DV', 'TR'),
  MI: only('TR'),
  MO: only('LT', 'CC', 'DV', 'TR'),
  MN: only('TR'),
  MT: only('O', 'TR'),
  NC: only('LT', 'CC', 'DV', 'TR'),
  ND: only('O'),
  NE: all(),                                       // Wheel Tax - all vehicle types
  NH: only('T', 'TR'),
  NJ: only('CC', 'PV', 'LT', 'SUV', 'TR'),
  NM: only('DV', 'T', 'CC', 'LT', 'O', 'TR'),
  NY: allExcept('M', 'T'),
  OH: only('LT', 'CC', 'DV'),
  OK: all(),                                       // Alt Fuel / EV fees - all vehicle types
  OR: only('CC', 'DV', 'TR'),
  PA: only('LT', 'CC', 'DV', 'O', 'T', 'TR'),
  RI: allExcept('M', 'T'),
  SC: only('LT', 'CC', 'DV', 'TR'),
  SD: only('PV', 'SUV', 'LT', 'CC', 'DV', 'TR'),
  TX: all(),                                       // Registration + Registration Tax (not specified)
  UT: only('LT', 'CC', 'DV', 'T', 'TR'),
  VT: only('LT', 'CC', 'DV', 'T', 'TR', 'O'),      // Reg & State Tax + Warranty Fee
  WI: only('T', 'TR', 'CC', 'DV', 'LT'),
  WV: only('TR', 'CC', 'DV', 'LT'),
  WY: all(),                                       // State Fee - all vehicle types
};

export const WEIGHT_REQUIREMENTS = {
  CT: only('LT', 'CC', 'DV'),
  KS: allExcept('M'),
  LA: only('LT', 'CC', 'DV'),
  NJ: only('LT', 'CC', 'DV'),
  TN: allExcept('T', 'M'),
};

/**
 * States that need <GVWR> — the gross vehicle weight *rating* — as its own
 * data point, separate from <GVW> and <Weight>.
 *
 * ATC rejects the request outright when it is missing. A Connecticut pickup
 * came back as:
 *
 *   Required data missing in path, for Registration Fee
 *   "//ServiceRequest/Vehicle/GVWR"
 *
 * with <Weight> and <GVW> both populated at 6500 — which is why neither table
 * above can stand in for this one.
 *
 * Ported from `stateSpecificRequirements` in `templates/fees.html`, the only
 * place in this codebase that has ever asked a user for GVWR: the Jinja Fees
 * page renders a dynamic `gvwr` input for these states, and fees.py has always
 * read `options['gvwr']` to fill the tag. The React form had no such field and
 * fees/api.js sent no such key, so every quote from the portal left <GVWR> off
 * the request for these state/vehicle-type combinations.
 *
 * The Jinja map is keyed by dropdown *label*, so labels that collapse to the
 * same ATC code are unioned here — notably "Truck" maps to LT, the same code as
 * "Light Truck" (see VEHICLE_TYPE_ALIASES below), so a state listing GVWR under
 * either label requires it for LT.
 *
 * Where the Jinja map lists GVWR for Light Truck, the vehicle-type scope is
 * taken from WEIGHT_REQUIREMENTS above rather than from the seven labels that
 * map happens to enumerate: it has no Cab & Chassis key at all, and a CT cab &
 * chassis fails exactly the way a CT pickup does.
 */
export const GVWR_REQUIREMENTS = {
  CT: only('LT', 'CC', 'DV'),   // fees.html "Light Truck": ["GVWR", "ModelYear"]
  KS: allExcept('M'),           // fees.html default + every label but Motorcycle
  LA: only('LT', 'CC', 'DV'),   // fees.html "Light Truck": ["MSRP", "GVWR"]
  NJ: only('LT', 'CC', 'DV'),   // fees.html "Light Truck": ["MSRP", "GVWR", "GVW"]
  TN: allExcept('T', 'M'),      // fees.html default + every label but Motorcycle
  VA: only('LT'),               // fees.html "Truck": ["GVWR"] ("Truck" -> LT)
};

export const MPG_REQUIREMENTS = {
  DC: all(),                                       // District Sales Tax - all vehicle types
  OR: allExcept('T'),                              // Registration (PV SUV LT) + Title (all except T)
};

// Full state names -> abbreviations. The ZIP decode populates
// locationInfo.state with full names in practice, but the abbreviation is
// accepted too so this works wherever the value comes from.
const STATE_NAME_TO_ABBR = {
  ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR',
  CALIFORNIA: 'CA', COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE',
  'DISTRICT OF COLUMBIA': 'DC', 'WASHINGTON DC': 'DC', 'WASHINGTON D.C.': 'DC',
  FLORIDA: 'FL', GEORGIA: 'GA', HAWAII: 'HI', IDAHO: 'ID',
  ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA', KANSAS: 'KS',
  KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD',
  MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN',
  MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE',
  NEVADA: 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK', OREGON: 'OR',
  PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT',
  VERMONT: 'VT', VIRGINIA: 'VA', WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV', WISCONSIN: 'WI', WYOMING: 'WY',
};

export const normalizeState = (state) => {
  if (state === null || state === undefined) return '';
  const st = String(state).trim().toUpperCase();
  if (st.length > 2) return STATE_NAME_TO_ABBR[st] || st;
  return st;
};

// Vehicle type labels -> ATC legend codes.
//
// This deliberately mirrors the frontend's own VEHICLE_TYPE_MAPPER
// (components/fees/contants, which docs/api.js also imports) rather than
// the bot's `_VEHICLE_TYPE_ALIASES`, because the mapper is what decides
// the <VehicleType> code actually sent on the wire — and that code is
// what the bot evaluates its rules against. Notably the frontend maps
// "Truck" -> LT while the bot's own alias table maps the word "TRUCK" ->
// TR; following the mapper keeps the asterisk shown here and the rule
// applied server-side in agreement.
const VEHICLE_TYPE_ALIASES = {
  // Fees dropdown values (components/fees/contants VEHICLE_TYPE keys)
  'PASSENGER VEHICLE': 'PV',
  'LIGHT TRUCK': 'LT',
  MOTORCYCLE: 'M',
  TRAILER: 'T',
  MOTORHOME: 'O',
  'CAB & CHASSIS': 'CC',
  'SPORTS UTILITY VEHICLE': 'SUV',
  'MULTIPURPOSE PASSENGER VEHICLE': 'SUV',
  TRUCK: 'LT',
  // Display-label variants
  'PICKUP TRUCK': 'LT',
  PICKUP: 'LT',
  'SPORT UTILITY VEHICLE': 'SUV',
};

// ATC legend codes, plus the four that appear in the guide's requirement
// tables without being in its legend.
const KNOWN_CODES = new Set([
  'PV', 'LT', 'M', 'T', 'O', 'CC', 'SUV', 'TR', 'DV', 'PU', '5W', 'CR',
]);

/**
 * Resolve a dropdown value (or a raw ATC code) to the code the bot will
 * actually evaluate, returning '' when it cannot be resolved.
 *
 * Anything the mapper does not recognise resolves to '' rather than being
 * passed through verbatim, because that is what reaches the bot: both
 * fees/api.js and docs/api.js send `VEHICLE_TYPE_MAPPER[vehicleType]`,
 * which is `undefined` for an unrecognised label, and the bot then applies
 * its conservative "type unresolved, so assume the rule applies" default.
 *
 * The live case is "Other" on the Docs / Refi-Docs dropdown: it is in
 * docs/constants VEHICLE_TYPE but not in the fees VEHICLE_TYPE_MAPPER that
 * docs/api.js imports, so `<VehicleType>` goes out blank. Treating it as
 * unresolved here keeps this side from asking for less than the bot will
 * demand. (If api.js is fixed to send 'O' — docs/constants
 * VEHICLE_TYPE_MAP already maps 'Other' -> 'O' — add OTHER: 'O' to the
 * alias table above and the requirement narrows to the states listing O.)
 */
export const normalizeVehicleType = (vehicleType) => {
  if (vehicleType === null || vehicleType === undefined) return '';
  const vt = String(vehicleType).trim().toUpperCase();
  if (vt === '') return '';
  if (VEHICLE_TYPE_ALIASES[vt]) return VEHICLE_TYPE_ALIASES[vt];
  return KNOWN_CODES.has(vt) ? vt : '';
};

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

/**
 * Mirror of `_rule_applies` in state_requirements.py.
 *
 * When the vehicle type cannot be resolved we cannot rule the requirement
 * out, so it is treated as applicable — same conservative default as the
 * bot. This matters: it means the portal can only ever ask for MORE than
 * the bot would bounce on, never less, so no submission slips through to
 * a `warning_response()`. In practice the window is small, since
 * Vehicle Type is itself a required field on every form.
 */
const ruleApplies = (rule, vehicleType) => {
  const [kind, codes] = rule;
  const vt = normalizeVehicleType(vehicleType);

  if (vt === '') return true;
  if (kind === ALL) return true;
  if (kind === ALL_EXCEPT) return !codes.has(vt);
  return codes.has(vt);
};

/**
 * Which of GVW / Vehicle Weight / GVWR / MPG the target state requires for
 * this vehicle type. Returns all false when no state has been selected, so the
 * default form has no extra required fields.
 *
 * @param {string} state - full name ("Oregon") or abbreviation ("OR")
 * @param {string} vehicleType - dropdown value or ATC code
 * @returns {{gvw: boolean, weight: boolean, gvwr: boolean, mpg: boolean}}
 */
export const getStateRequiredSpecs = (state, vehicleType) => {
  const none = { gvw: false, weight: false, gvwr: false, mpg: false };
  if (isBlank(state)) return none;

  const st = normalizeState(state);

  return {
    gvw: GVW_REQUIREMENTS[st] ? ruleApplies(GVW_REQUIREMENTS[st], vehicleType) : false,
    weight: WEIGHT_REQUIREMENTS[st] ? ruleApplies(WEIGHT_REQUIREMENTS[st], vehicleType) : false,
    gvwr: GVWR_REQUIREMENTS[st] ? ruleApplies(GVWR_REQUIREMENTS[st], vehicleType) : false,
    mpg: MPG_REQUIREMENTS[st] ? ruleApplies(MPG_REQUIREMENTS[st], vehicleType) : false,
  };
};

// Which redux `vehicleInfo` key carries each spec on each form, how to label
// it, and which fields the bot will accept as satisfying it.
//
// `key` drives the red asterisk: it marks the field the state is really
// asking for, so the form always tells the user which figure the DMV wants.
// `satisfiedBy` drives submit-time validation, and mirrors the leniency the
// receiving lambda actually applies — so the form asks for the right number
// without blocking a request the bot would have accepted.
//
// The two sides differ, which is why this is per-form:
//
//   Fees (DMV-bot-fees, DMV-bot-refi-fees) cross-backfill GVW and Weight
//   (`GVW, Weight = (GVW or Weight), (Weight or GVW)`), so either one
//   satisfies both. DMV-bot-fees also resolves MPG as
//   `CombinedMPG or CityMPG or HighwayMPG`, so any of the three counts.
//
//   Docs (DMV-bot-docs, DMV-bot-refi-docs) do NO backfill — they pass
//   `gvw=`, `weight=`, `mpg=` straight through — so each is required
//   independently there.
const FORM_FIELD_MAP = {
  fees: {
    gvw: {
      key: 'gvw',
      label: 'GVW',
      satisfiedBy: ['gvw', 'vehicleWeight'],
    },
    weight: {
      key: 'vehicleWeight',
      label: 'Vehicle Weight',
      satisfiedBy: ['vehicleWeight', 'gvw'],
    },
    // GVWR is the manufacturer's rating, not a measured weight, so nothing
    // else satisfies it: GVW and Vehicle Weight are different numbers and
    // substituting either would quote the wrong weight bracket rather than
    // fail. fees.py reads it from the lowercase `gvwr` key (the name the Jinja
    // page's dynamic field has always used) and emits <GVWR> only when it is
    // non-empty, so an unrequired blank still costs nothing.
    gvwr: {
      key: 'gvwr',
      label: 'GVWR',
      satisfiedBy: ['gvwr'],
    },
    // <MPG> is a single value in the ATC spec; the asterisk lands on City
    // MPG because that is what fees/api.js sends as <MPG>, but Highway and
    // Combined satisfy the requirement too. `mpg` is the legacy combined
    // value the VIN decode can populate, and api.js falls back to it.
    mpg: {
      key: 'cityMpg',
      label: 'MPG (City)',
      satisfiedBy: ['cityMpg', 'highwayMpg', 'combinedMpg', 'mpg'],
    },
  },
  // No `gvwr` entry: the Docs / Refi-Docs forms have no GVWR field and
  // redux-state/docs/api.js sends no such key, so there is nothing for the
  // requirement to land on. A spec with no entry here is skipped rather than
  // reported against an undefined field key — see getRequiredVehicleSpecFields.
  docs: {
    gvw: { key: 'gvw', label: 'GVW', satisfiedBy: ['gvw'] },
    weight: { key: 'vehicleWeight', label: 'Weight', satisfiedBy: ['vehicleWeight'] },
    mpg: { key: 'mpg', label: 'MPG', satisfiedBy: ['mpg'] },
  },
};

/**
 * Per-form view of the same rules: maps each required spec onto the redux
 * field key that form uses.
 *
 * A spec the form has no field for is reported in the booleans but left out of
 * `fields`, so it neither picks up an asterisk nor blocks submit.
 *
 * @param {'fees'|'docs'} formKind
 * @param {string} state
 * @param {string} vehicleType
 * @returns {{gvw: boolean, weight: boolean, gvwr: boolean, mpg: boolean, fields: Array<{spec: string, key: string, label: string}>}}
 */
export const getRequiredVehicleSpecFields = (formKind, state, vehicleType) => {
  const specs = getStateRequiredSpecs(state, vehicleType);
  const map = FORM_FIELD_MAP[formKind] || FORM_FIELD_MAP.docs;

  const fields = Object.keys(specs)
    .filter((spec) => specs[spec] && map[spec])
    .map((spec) => ({ spec, ...map[spec] }));

  return { ...specs, fields };
};

/**
 * Validation helper shared by the Fees and Docs branches of
 * FormValidation. Writes `"<Label> is required for <State>"` into `errors`
 * for each spec the state requires that no acceptable field satisfies.
 *
 * A spec counts as satisfied when ANY of its `satisfiedBy` fields has a
 * value, which is what keeps this from blocking a request the receiving
 * lambda would have processed: on Fees, Vehicle Weight backfills GVW and
 * any of the three MPG figures satisfies <MPG>. The asterisk still marks
 * the field the state actually wants — this only governs whether submit is
 * blocked.
 *
 * Never overwrites an error already set by the static required-field
 * lists, so a field that is unconditionally required keeps its original
 * message.
 */
export const applyStateSpecRequirements = (formKind, formData = {}, errors = {}) => {
  const { fields } = getRequiredVehicleSpecFields(
    formKind,
    formData.state,
    formData.vehicleType
  );

  fields.forEach(({ key, label, satisfiedBy }) => {
    if (errors[key]) return;
    const satisfied = (satisfiedBy || [key]).some((k) => !isBlank(formData[k]));
    if (!satisfied) {
      errors[key] = `${label} is required for ${formData.state}`;
    }
  });

  return errors;
};

export default getStateRequiredSpecs;
